// src/components/Organization/EmailIntegrationSettings.tsx

// What? Organization-level email integration configuration
// Why? To allow org admins to configure email provider and sender settings
// How? Settings card with provider selection, credentials, and test functionality

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Alert,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Switch,
  Radio,
  RadioGroup,
  CircularProgress,
} from "@mui/joy";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import SaveIcon from "@mui/icons-material/Save";
import { useAuthFetch } from "../../utils/useAuthFetch";
import { invalidateOrgEmailStatus } from "../../hooks/useOrgEmailStatus";

interface EmailIntegrationSettingsProps {
  organizationId: string;
}

interface EmailConfig {
  provider: string;
  graph_tenant_id?: string;
  graph_client_id?: string;
  graph_client_secret_name?: string;
  default_sender_address?: string;
  default_sender_display_name?: string;
  email_enabled: boolean;
  rate_limit_per_hour: number;
  allow_external_automations: boolean;
}

const DEFAULT_CONFIG: EmailConfig = {
  provider: "platform",
  default_sender_address: "",
  default_sender_display_name: "",
  email_enabled: true,
  rate_limit_per_hour: 50,
  allow_external_automations: true,
};

const EmailIntegrationSettings: React.FC<EmailIntegrationSettingsProps> = ({
  organizationId,
}) => {
  const authFetch = useAuthFetch();
  const [config, setConfig] = useState<EmailConfig>(DEFAULT_CONFIG);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");
  const [feedback, setFeedback] = useState<{
    type: "success" | "danger";
    message: string;
  } | null>(null);

  const normalizedTestRecipient = testRecipient.trim();
  const canSendTest =
    configured &&
    Boolean(normalizedTestRecipient) &&
    normalizedTestRecipient.includes("@");

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const data = await authFetch(
        `/api/email/organization/${organizationId}/config`,
        "fetch email config",
      );
      if (data?.configured) {
        setConfig({ ...DEFAULT_CONFIG, ...data.config });
        setConfigured(true);
      }
    } catch {
      // Not configured yet -- use defaults
    } finally {
      setLoading(false);
    }
  }, [authFetch, organizationId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await authFetch(
        `/api/email/organization/${organizationId}/config`,
        "save email config",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(config),
        },
      );
      setConfigured(true);
      invalidateOrgEmailStatus(organizationId);
      setFeedback({ type: "success", message: "Email configuration saved" });
    } catch (err) {
      setFeedback({
        type: "danger",
        message: `Failed to save: ${err}`,
      });
    } finally {
      setSaving(false);
      setTimeout(() => setFeedback(null), 5000);
    }
  };

  const handleTest = async () => {
    if (!normalizedTestRecipient || !normalizedTestRecipient.includes("@")) {
      setFeedback({
        type: "danger",
        message: "Enter a valid recipient email first.",
      });
      return;
    }
    setTesting(true);
    setFeedback(null);
    try {
      const result = await authFetch(
        `/api/email/organization/${organizationId}/test`,
        "fetch email test result",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ recipient_email: normalizedTestRecipient }),
        },
      );
      if (result?.success) {
        setFeedback({
          type: "success",
          message: `Test email sent to ${normalizedTestRecipient} via ${result.provider}.`,
        });
      } else {
        setFeedback({
          type: "danger",
          message: `Test failed: ${result?.error || "Unknown error"}`,
        });
      }
    } catch (err) {
      setFeedback({ type: "danger", message: `Test failed: ${err}` });
    } finally {
      setTesting(false);
      setTimeout(() => setFeedback(null), 8000);
    }
  };

  const updateField = <K extends keyof EmailConfig>(
    field: K,
    value: EmailConfig[K],
  ) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size="sm" />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2.5}>
          {/* Header */}
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}
          >
            <EmailIcon sx={{ color: "primary.500" }} />
            <Typography level="title-lg">Email Integration</Typography>
            {configured && (
              <Typography
                level="body-xs"
                sx={{
                  color: config.email_enabled ? "success.600" : "neutral.500",
                  ml: "auto",
                }}
              >
                {config.email_enabled ? "Active" : "Disabled"}
              </Typography>
            )}
          </Box>

          {/* Provider Selection */}
          <FormControl>
            <FormLabel>Email Provider</FormLabel>
            <RadioGroup
              value={config.provider}
              onChange={(e) => updateField("provider", e.target.value)}
              sx={{ gap: 1 }}
            >
              <Radio value="platform" label="Platform (X-Machine)" size="sm" />
              <Radio value="graph" label="Microsoft 365" size="sm" />
            </RadioGroup>
          </FormControl>

          {/* MS365 fields */}
          {config.provider === "graph" && (
            <Stack
              spacing={2}
              sx={{
                pl: 1,
                borderLeft: "2px solid",
                borderColor: "primary.200",
              }}
            >
              <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                Microsoft Graph configuration (client credentials flow)
              </Typography>
              <Stack direction="row" spacing={2}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Tenant ID</FormLabel>
                  <Input
                    size="sm"
                    placeholder="c1fd05f4-..."
                    value={config.graph_tenant_id || ""}
                    onChange={(e) =>
                      updateField("graph_tenant_id", e.target.value)
                    }
                  />
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Client ID</FormLabel>
                  <Input
                    size="sm"
                    placeholder="0501f640-..."
                    value={config.graph_client_id || ""}
                    onChange={(e) =>
                      updateField("graph_client_id", e.target.value)
                    }
                  />
                </FormControl>
              </Stack>
              <FormControl>
                <FormLabel>Client Secret Name</FormLabel>
                <Input
                  size="sm"
                  placeholder="GRAPH-EMAIL-SECRET-SSI"
                  value={config.graph_client_secret_name || ""}
                  onChange={(e) =>
                    updateField("graph_client_secret_name", e.target.value)
                  }
                />
                <Typography
                  level="body-xs"
                  sx={{ color: "text.tertiary", mt: 0.5 }}
                >
                  Reference to Key Vault secret (not the secret itself)
                </Typography>
              </FormControl>
            </Stack>
          )}

          {/* Common fields */}
          <Stack direction="row" spacing={2}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Default Sender Address</FormLabel>
              <Input
                size="sm"
                type="email"
                placeholder="notifications@company.com"
                value={config.default_sender_address || ""}
                onChange={(e) =>
                  updateField("default_sender_address", e.target.value)
                }
              />
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Sender Display Name</FormLabel>
              <Input
                size="sm"
                placeholder="HR System"
                value={config.default_sender_display_name || ""}
                onChange={(e) =>
                  updateField("default_sender_display_name", e.target.value)
                }
              />
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="flex-end">
            <FormControl sx={{ width: 140 }}>
              <FormLabel>Rate Limit</FormLabel>
              <Input
                size="sm"
                type="number"
                value={config.rate_limit_per_hour}
                onChange={(e) =>
                  updateField(
                    "rate_limit_per_hour",
                    parseInt(e.target.value) || 50,
                  )
                }
                endDecorator={<Typography level="body-xs">/hour</Typography>}
              />
            </FormControl>
            <FormControl>
              <Stack direction="row" spacing={1} alignItems="center">
                <Switch
                  checked={config.email_enabled}
                  onChange={(e) =>
                    updateField("email_enabled", e.target.checked)
                  }
                  size="sm"
                />
                <FormLabel sx={{ m: 0 }}>
                  {config.email_enabled
                    ? "Email sending enabled"
                    : "Email sending disabled"}
                </FormLabel>
              </Stack>
            </FormControl>
          </Stack>

          {/* Automation lockdown: UI toggles "restrict", backend field is
              the inverse "allow" -- default permissive so existing orgs
              keep their current behaviour. */}
          <FormControl>
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={!config.allow_external_automations}
                onChange={(e) =>
                  updateField("allow_external_automations", !e.target.checked)
                }
                size="sm"
              />
              <FormLabel sx={{ m: 0 }}>
                Restrict automations to org-verified domains
              </FormLabel>
            </Stack>
            <FormHelperText>
              When on, SEND_EMAIL actions and BKG digests can only send to email
              addresses whose domain is in your organization&apos;s verified
              domains. Turn on for stricter compliance.
            </FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>Test recipient</FormLabel>
            <Input
              size="sm"
              type="email"
              placeholder="you@example.com"
              value={testRecipient}
              onChange={(e) => setTestRecipient(e.target.value)}
            />
            <FormHelperText>
              Send a one-off test email to verify your integration is working.
            </FormHelperText>
          </FormControl>

          {/* Feedback */}
          {feedback && (
            <Alert color={feedback.type} size="sm">
              {feedback.message}
            </Alert>
          )}

          {/* Actions */}
          <Stack direction="row" spacing={1.5}>
            <Button
              size="sm"
              startDecorator={<SaveIcon />}
              loading={saving}
              onClick={handleSave}
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="outlined"
              startDecorator={<SendIcon />}
              loading={testing}
              onClick={handleTest}
              disabled={!canSendTest}
            >
              Send Test Email
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default EmailIntegrationSettings;
